/**
 * billingClient — the typed fetch wrappers over the payment user plane
 * (`/api/v1`, session cookie; Plan 02 D1). Applications inside iframes ride
 * the same-origin app domains that proxy `/api`; the Tauri shell rides the
 * Bearer token — both are plain same-origin fetches from the client's seat,
 * so the base path is relative and never names a host.
 *
 * Error envelopes follow the 06 §6 shape: `{ error: { code, message } }` —
 * surfaced as `BillingError` with the HTTP status attached (402 is data, not
 * an exception path, for the callers that want the quota payload).
 */
import type {
  CardSession,
  CheckoutAnswer,
  CouponEvaluation,
  CreditNoteView,
  EntitlementEntry,
  InvoicePage,
  PaymentMethodView,
  PaymentView,
  Plan,
  RecoveryAction,
  SubscriptionSummary,
  UsageResponse,
} from "../types";

export class BillingError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "BillingError";
    this.status = status;
    this.code = code;
  }
}

const BASE = "/api/v1";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      credentials: "same-origin",
      ...init,
      headers: {
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw new BillingError("The billing service is unreachable.", 0, "NETWORK_ERROR");
  }
  if (!response.ok) {
    let code = String(response.status);
    let message = response.statusText || "The request failed.";
    try {
      const envelope = (await response.json()) as { error?: { code?: string; message?: string } };
      if (envelope?.error?.code) code = envelope.error.code;
      if (envelope?.error?.message) message = envelope.error.message;
    } catch {
      // A non-JSON error body still becomes a BillingError below.
    }
    throw new BillingError(message, response.status, code);
  }
  return (await response.json()) as T;
}

function post(path: string, body?: unknown, headers?: Record<string, string>): Promise<unknown> {
  return request(path, { method: "POST", body: body ? JSON.stringify(body) : undefined, headers });
}

/** A fresh idempotency key per checkout attempt (06 §4): retries are safe. */
function idempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const billingClient = {
  /** The one catalog: per-service free thresholds + AI packs. */
  async getPlans(serviceId?: string): Promise<{ plans: Plan[] }> {
    const query = serviceId ? `?serviceId=${encodeURIComponent(serviceId)}` : "";
    return request(`/plans${query}`);
  },

  /** The caller's display snapshots for every billable service. */
  async getEntitlements(): Promise<{ services: EntitlementEntry[] }> {
    return request("/entitlements/me");
  },

  /** The aggregated usage meters (storage ledger, AI tokens, bandwidth). */
  async getUsage(): Promise<UsageResponse> {
    return request("/usage/me");
  },

  /** The caller's subscription rows (quota summaries on live rows only). */
  async getSubscription(): Promise<{ subscriptions: SubscriptionSummary[] }> {
    return request("/subscriptions/me");
  },

  /** The pre-checkout coupon evaluation. */
  async validateCoupon(code: string, planId: string): Promise<CouponEvaluation> {
    return post("/coupons/validate", { code, planId }) as Promise<CouponEvaluation>;
  },

  /** Open a checkout round — provider door + SSE stream id in the answer. */
  async startCheckout(
    planId: string,
    options: { coupon?: string; serviceId?: string } = {}
  ): Promise<CheckoutAnswer> {
    return post(
      "/checkout",
      { planId, serviceId: options.serviceId, couponCode: options.coupon || undefined },
      { "idempotency-key": idempotencyKey() }
    ) as Promise<CheckoutAnswer>;
  },

  /** One payment's live state (re-verifies with the provider server-side). */
  async getPayment(paymentId: string): Promise<PaymentView> {
    return request(`/payments/${encodeURIComponent(paymentId)}`);
  },

  /** Cancel an open checkout round. */
  async cancelCheckout(paymentId: string): Promise<PaymentView> {
    return post(`/payments/${encodeURIComponent(paymentId)}/cancel`, {}) as Promise<PaymentView>;
  },

  /**
   * Subscribe to the payment's status stream (06 §2.2 SSE). Returns the
   * unsubscribe function; the stream replays missed events on reconnect via
   * Last-Event-ID, so a flaky socket never loses the terminal state.
   */
  subscribePaymentEvents(
    paymentId: string,
    onEvent: (event: { paymentId: string; status: string; subscriptionId: string | null; timestamp: string }) => void
  ): () => void {
    const source = new EventSource(`${BASE}/payments/${encodeURIComponent(paymentId)}/events`);
    source.addEventListener("payment_status", (event) => {
      try {
        onEvent(JSON.parse((event as MessageEvent).data));
      } catch {
        // A malformed frame is skipped; the next one re-syncs via the stream.
      }
    });
    return () => source.close();
  },

  /** The user's invoice documents (cursor-paginated). */
  async listInvoices(cursor?: string, limit?: number): Promise<InvoicePage> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (limit !== undefined) params.set("limit", String(limit));
    const query = params.toString();
    return request(`/invoices${query ? `?${query}` : ""}`);
  },

  /** Download an invoice PDF — the bytes land as a same-document anchor click. */
  async downloadInvoice(invoiceId: string): Promise<void> {
    const response = await fetch(`${BASE}/invoices/${encodeURIComponent(invoiceId)}/download`, {
      credentials: "same-origin",
    });
    if (!response.ok) throw new BillingError("The invoice could not be downloaded.", response.status, "DOWNLOAD_FAILED");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${invoiceId}.pdf`;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  /** The user's credit notes (correction documents). */
  async listCreditNotes(cursor?: string, limit?: number): Promise<{ creditNotes: CreditNoteView[]; hasMore: boolean }> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (limit !== undefined) params.set("limit", String(limit));
    const query = params.toString();
    return request(`/credit-notes${query ? `?${query}` : ""}`);
  },

  /** Download a credit note PDF (credit-note ids are `CN-…` strings). */
  async downloadCreditNote(noteId: string): Promise<void> {
    const response = await fetch(`${BASE}/credit-notes/${encodeURIComponent(noteId)}/download`, {
      credentials: "same-origin",
    });
    if (!response.ok) throw new BillingError("The credit note could not be downloaded.", response.status, "DOWNLOAD_FAILED");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${noteId}.pdf`;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  /** The caller's stored payment methods (tokens never leave the server). */
  async listPaymentMethods(): Promise<{ paymentMethods: PaymentMethodView[] }> {
    return request("/payment-methods");
  },

  /** Open the SCA card-add session. */
  async createCardSession(provider?: string): Promise<CardSession> {
    return post("/payment-methods/sessions", { provider: provider || undefined }) as Promise<CardSession>;
  },

  /**
   * Detach a payment method. `confirm` rides in the body when the server's
   * 409 PAYMENT_METHOD_ATTACHED asked for explicit consent.
   */
  async deletePaymentMethod(methodId: string, confirm = false): Promise<{ id: string; status: string }> {
    return request(`/payment-methods/${encodeURIComponent(methodId)}`, {
      method: "DELETE",
      body: JSON.stringify({ confirm }),
    });
  },

  /** One lifecycle action on an owned subscription (06 §2). */
  async lifecycleAction(
    subscriptionId: string,
    kind: "cancel" | "resume" | "pause" | "upgrade" | "downgrade",
    body: Record<string, unknown> = {}
  ): Promise<Record<string, unknown>> {
    return post(`/subscriptions/${encodeURIComponent(subscriptionId)}/${kind}`, body) as Promise<Record<string, unknown>>;
  },

  /** Open a trial on a service's paid plan (04 §6). */
  async startTrial(serviceId: string, planId: string): Promise<{ subscriptionId: string; trialId: string; trialEndAt: string }> {
    return post("/trials", { serviceId, planId }) as Promise<{ subscriptionId: string; trialId: string; trialEndAt: string }>;
  },

  /** The required-action list (dunning / grace) the Tauri app polls on open. */
  async getRecoveryActions(): Promise<{ actions: RecoveryAction[] }> {
    return request("/billing/recovery-actions");
  },

  /** The state of the user's KVKK erasure request. */
  async getDataDeletion(): Promise<{ requestId: string | null; status: string }> {
    return request("/privacy/data-deletion");
  },

  /** File the KVKK erasure request (idempotent). */
  async requestDataDeletion(): Promise<{ requestId: string; status: string }> {
    return post("/privacy/data-deletion", {}) as Promise<{ requestId: string; status: string }>;
  },

  /* ------------------------- development-only doors -------------------------
   * The local-development provider double (`mock.provider.ts`) answers
   * checkouts with a `mock://` URL — no hosted provider page exists to open.
   * These doors drive the SAME server pipeline the real provider's webhook
   * would (signature → dedup → outbox → worker → SSE), from one button press.
   * The endpoints only exist outside production, and a `mock://` checkout URL
   * can only ever be minted there — these are unreachable dead code in a
   * deployed build. */

  /** Simulate the provider's verdict on a mock checkout round. */
  async simulateMockPayment(
    paymentId: string,
    outcome: "success" | "failed"
  ): Promise<{ paymentId: string; status: string; subscriptionId: string | null; invoiceId: string | null }> {
    return post(`/dev/mock-checkouts/${encodeURIComponent(paymentId)}/complete`, { outcome }) as Promise<{
      paymentId: string;
      status: string;
      subscriptionId: string | null;
      invoiceId: string | null;
    }>;
  },

  /** Complete a mock card-add session with a test card (the list then shows it). */
  async completeMockCardSession(
    sessionToken: string,
    card: { brand?: string; last4?: string } = {}
  ): Promise<{ id: string | null; status: string; reused: boolean }> {
    return post(`/dev/mock-card-sessions/${encodeURIComponent(sessionToken)}/complete`, card) as Promise<{
      id: string | null;
      status: string;
      reused: boolean;
    }>;
  },
};
